import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import LocationPreferenceDropdown from 'components/LocationPreferenceDropdown';
import PageTitleBar from 'components/PageTitleBar';
import BattleSlotsInventory from 'components/WStoreBattleSlotsInventory';
import WStoreCategoriesNew from 'components/WStoreCategoriesNew';
import { PROPERTY_CHICAGO_SOUTHLAND } from 'utils/constants';
import { errorShape } from 'utils/shapes';
import WStoreIcon from 'resources/FeatureIcons/Button-WStore.svg';
import WStoreAlertNew from '../WStoreAlertNew';
import WStoreDiscover from '../WStoreDiscover';
import WStoreModalNew from '../WStoreModalNew';
import WStorePagination from '../WStorePagination';
import './WStoreViewNew.css';

function renderWStorePreferenceDropdown(props) {
  return (
    !!props.user && (
      <LocationPreferenceDropdown
        user={props.user}
        preferredLocation={props.preferredLocation}
        onSelectLocation={props.onSelectLocation}
        onCloseSelectPreference={props.onCloseSelectPreference}
      />
    )
  );
}

function renderBattleSlotsInventoryModal(props) {
  const battleSlotsCategory = props.categories.filter(
    category => category.name === 'Battle Challenge'
  );

  return (
    <BattleSlotsInventory
      onClose={props.onCloseBattleSlotsInventory}
      show={props.showBattleSlotsInventory}
      onBuyMore={props.onSelectCategory}
      inventory={props.battleSlotsInventory}
      battleSlotsCategory={battleSlotsCategory}
      battleSlotsInfoText={props.battleSlotsInfoText}
    />
  );
}

function renderHeader(props) {
  const { loading, editingWStorePreference } = props;
  return (
    <PageTitleBar title="WStore" icon={WStoreIcon}>
      {renderWStorePreferenceDropdown(props)}
      {!loading &&
        !editingWStorePreference &&
        renderBattleSlotsInventoryModal(props)}
    </PageTitleBar>
  );
}

function renderCategoriesSelection({
  categories,
  onSelectCategory,
  onSelectHotPicks,
  onSelectAllCategory,
  preferredLocation,
  categoryFilter,
  showDiscover,
  onSelectDiscover,
  enableDiscover
}) {
  return (
    !!categories &&
    categories.length > 0 && (
      <WStoreCategoriesNew
        location={preferredLocation}
        categories={categories}
        onSelectHotPicks={onSelectHotPicks}
        onSelectAllCategory={onSelectAllCategory}
        onSelectCategory={onSelectCategory}
        showDiscover={showDiscover}
        enableDiscover={enableDiscover}
        onSelectDiscover={onSelectDiscover}
        currCategoryFilter={categoryFilter}
      />
    )
  );
}

function renderPageContent(props, scrollRef) {
  const {
    userWScore,
    patronAssets,
    items,
    categoryFilter,
    alertMsg,
    selectedItem,
    purchaseWithTrade,
    purchaseRequested,
    purchaseConfirmation,
    purchaseResponseMsg,
    purchaseEmailMsg,
    onSelectItem,
    onSelectItemWithTrade,
    onPurchase,
    onClose,
    editingWStorePreference,
    exchangeRates,
    showDiscover,
    onSelectHotPicks,
    onSelectCategory,
    user
  } = props;

  if (editingWStorePreference) {
    return null;
  } else if (items && items.length) {
    return (
      <React.Fragment>
        <div className="wstore-content" ref={scrollRef}>
          <WStoreModalNew
            selectedItem={selectedItem}
            userWScore={userWScore}
            patronAssets={patronAssets}
            onPurchase={onPurchase}
            purchaseWithTrade={purchaseWithTrade}
            purchaseRequested={purchaseRequested}
            purchaseConfirmation={purchaseConfirmation}
            purchaseResponseMsg={purchaseResponseMsg}
            purchaseEmailMsg={purchaseEmailMsg}
            hidden={!!alertMsg}
            onClose={onClose}
            exchangeRates={exchangeRates}
            preferredLocation={user.preferredLocation}
          />
          {alertMsg?.body && (
            <WStoreAlertNew alertMsg={alertMsg} onClose={onClose} />
          )}
          {showDiscover ? (
            <WStoreDiscover
              patronAssets={patronAssets}
              wstoreItems={items}
              onSelectItem={onSelectItem}
              onSelectItemWithTrade={onSelectItemWithTrade}
              key={categoryFilter.value}
              exchangeRates={exchangeRates}
              onSelectHotPicks={onSelectHotPicks}
              onSelectCategory={onSelectCategory}
              preferredLocation={user.preferredLocation}
            />
          ) : (
            <WStorePagination
              patronAssets={patronAssets}
              wstoreItems={items}
              onSelectItem={onSelectItem}
              onSelectItemWithTrade={onSelectItemWithTrade}
              key={categoryFilter.value}
              exchangeRates={exchangeRates}
              showVaultBalance={true}
              preferredLocation={user.preferredLocation}
            />
          )}
        </div>
      </React.Fragment>
    );
  } else {
    return (
      <div className="wstore-category-empty">
        There are no items currently available in this category at the selected
        location.
        <br />
        Please check another category or location.
      </div>
    );
  }
}

function WStoreViewNew(props) {
  const {
    loading,
    error,
    propertyEnabledWStore,
    chicagoWstore,
    preferredLocation,
    categoryFilter
  } = props;
  let selectedProperty = null;
  if (propertyEnabledWStore.length) {
    selectedProperty = propertyEnabledWStore.find(
      obj => obj.property === preferredLocation
    );
  }

  const isWStoreDisabledForSelectedProperty =
    (selectedProperty && !selectedProperty.cvWStoreEnabled) ||
    (selectedProperty?.property === PROPERTY_CHICAGO_SOUTHLAND &&
      !chicagoWstore?.remoteConfigEntry?.value);

  const scrollRef = useRef(null);
  useEffect(() => scrollRef.current?.scrollTo({ top: 0 }), [categoryFilter]);

  let content;
  if (loading) {
    content = null;
  } else if (error) {
    content = (
      <div className="alert alert-danger">
        There was a problem retrieving the list of WStore items. Please try
        again later.
      </div>
    );
  } else if (isWStoreDisabledForSelectedProperty) {
    content = (
      <div className="wstore-configuration-text">
        <div>The WStore is currently unavailable.</div>
        <div>Please check back later.</div>
      </div>
    );
  } else {
    content = renderPageContent(props, scrollRef);
  }
  return (
    <div className="wstore-view-new">
      {renderHeader(props)}
      {renderCategoriesSelection(props)}
      {content}
    </div>
  );
}

WStoreViewNew.propTypes = {
  loading: PropTypes.bool,
  error: errorShape,
  wstoreEnabled: PropTypes.bool,
  propertyEnabledWStore: PropTypes.arrayOf(PropTypes.object),
  wstoreReloadDate: PropTypes.string,
  userWScore: PropTypes.number,
  patronAssets: PropTypes.any,
  items: PropTypes.array,
  categories: PropTypes.array,
  showHotPicks: PropTypes.bool,
  onSelectItem: PropTypes.func,
  onSelectItemWithTrade: PropTypes.func,
  onSelectHotPicks: PropTypes.func,
  onSelectCategory: PropTypes.func,
  onSelectLocation: PropTypes.func,
  onPurchase: PropTypes.func,
  onClose: PropTypes.func,
  selectedItem: PropTypes.object,
  purchaseWithTrade: PropTypes.bool,
  purchaseRequested: PropTypes.bool,
  purchaseResponseMsg: PropTypes.string,
  purchaseEmailMsg: PropTypes.string,
  purchaseConfirmation: PropTypes.bool,
  categoryFilter: PropTypes.object,
  user: PropTypes.object,
  onEditLocationPreference: PropTypes.func,
  onCloseSelectPreference: PropTypes.func,
  showWStorePreferenceModal: PropTypes.bool,
  editingWStorePreference: PropTypes.bool,
  exchangeRates: PropTypes.shape({
    wscorePerNugget: PropTypes.number,
    wscorePerRuby: PropTypes.number,
    wscorePerSapphire: PropTypes.number,
    wscorePerWChip: PropTypes.number
  })
};

export default WStoreViewNew;
